/* eslint-disable sort-imports */
import { Button, Modal, notification, Spin, Table } from 'antd'
import React, { useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import {
  useCreateBannerMutation,
  useDeleteBannerMutation,
  useFetchBannerListQuery,
  useUpdateBannerMutation,
} from 'domain/Banner/bannerApi'

import Card from 'components/Card'
import Heading from 'components/Heading/Heading'
import withLayout from 'components/layout'
import BannerDrawer from './components/BannerDrawer'

import '../Admin/Admin.less'
import './BannerManagement.less'

const DATE_DISPLAY_FORMAT = 'MMM D, YYYY'

// ---------- Component ----------
const BannerManagement = () => {
  const { t } = useTranslation()

  const { data: banners = [], isFetching } = useFetchBannerListQuery()
  const [createBanner, { isLoading: isCreating }] = useCreateBannerMutation()
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation()
  const [deleteBanner, { isLoading: isDeleting }] = useDeleteBannerMutation()

  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectedBanner, setSelectedBanner] = useState(null)

  const isSubmitting = isCreating || isUpdating

  // ---------- Handlers ----------
  const handleCreate = () => {
    setSelectedBanner(null)
    setDrawerVisible(true)
  }

  const handleEdit = record => {
    setSelectedBanner(record)
    setDrawerVisible(true)
  }

  const handleDrawerClose = () => {
    setDrawerVisible(false)
    setSelectedBanner(null)
  }

  const handleDrawerSubmit = async payload => {
    const action = selectedBanner ? updateBanner : createBanner
    const successKey = selectedBanner ? 'updated' : 'created'
    const errorKey = selectedBanner ? 'updateFailed' : 'createFailed'

    try {
      await action(payload).unwrap()
      notification.success({
        message: t(`admin.bannerManagement.success.${successKey}`),
      })
      handleDrawerClose()
    } catch {
      notification.error({
        message: t(`admin.bannerManagement.error.${errorKey}`),
      })
    }
  }

  const handleDelete = record => {
    Modal.confirm({
      cancelText: t('admin.bannerManagement.deleteConfirm.cancel'),
      content: t('admin.bannerManagement.deleteConfirm.description'),
      okButtonProps: { danger: true, loading: isDeleting },
      okText: t('admin.bannerManagement.deleteConfirm.ok'),
      onOk: async () => {
        try {
          await deleteBanner(record.id).unwrap()
          notification.success({
            message: t('admin.bannerManagement.success.deleted'),
          })
        } catch {
          notification.error({
            message: t('admin.bannerManagement.error.deleteFailed'),
          })
        }
      },
      title: t('admin.bannerManagement.deleteConfirm.title'),
    })
  }

  // ---------- Table ----------
  const columns = [
    {
      dataIndex: 'title',
      render: title => (
        <div className="banner-management__table-title-box">{title}</div>
      ),
      title: t('admin.bannerManagement.tableColumns.headline'),
      width: 360,
    },
    {
      dataIndex: 'startDate',
      render: v => (v ? dayjs(v).format(DATE_DISPLAY_FORMAT) : '—'),
      title: t('admin.bannerManagement.tableColumns.startDate'),
      width: 130,
    },
    {
      dataIndex: 'endDate',
      render: v => (v ? dayjs(v).format(DATE_DISPLAY_FORMAT) : '—'),
      title: t('admin.bannerManagement.tableColumns.endDate'),
      width: 130,
    },
    {
      align: 'center',
      render: (_, record) => (
        <div className="banner-management__actions">
          <Button onClick={() => handleEdit(record)} size="small" type="link">
            {t('admin.bannerManagement.actions.edit')}
          </Button>
          <Button
            danger
            onClick={() => handleDelete(record)}
            size="small"
            type="link"
          >
            {t('admin.bannerManagement.actions.delete')}
          </Button>
        </div>
      ),
      title: t('admin.bannerManagement.tableColumns.actions'),
      width: 100,
    },
  ]

  // ---------- Render ----------
  return (
    <>
      {isSubmitting && (
        <div className="banner-management__loading-overlay">
          <Spin size="large" />
        </div>
      )}

      <div className="page__container">
        <div className="banner-management__header">
          <Heading level={2}>{t('admin.bannerManagement.title')}</Heading>

          <button
            className="admin-create-vessel-button"
            onClick={handleCreate}
            type="button"
          >
            {t('admin.bannerManagement.create')}
          </button>
        </div>

        <Card className="banner-management__card">
          <Spin spinning={isFetching}>
            <Table
              columns={columns}
              dataSource={banners}
              pagination={false}
              rowKey="id"
            />
          </Spin>
        </Card>

        <BannerDrawer
          banner={selectedBanner}
          banners={banners}
          onClose={handleDrawerClose}
          onSubmit={handleDrawerSubmit}
          submitting={isSubmitting}
          visible={drawerVisible}
        />
      </div>
    </>
  )
}

export default withLayout(BannerManagement)
